import { IconHeart } from './Icon';
import { toggleLike, useLiked, type MediaItem } from '../lib/media';
import { useSession } from '../lib/session';

/**
 * The heart — and the only place the like rules live.
 *
 * **Always visible, always with its count** (the Instagram shape): grey and
 * hollow means nobody here has liked it, red and filled means you have, and the
 * number beside it is the total either way. An earlier version rendered nothing
 * when the count was zero and nothing on your own photos, which was wrong in a
 * way that only showed up on real data — every photo in the library belonged to
 * the person looking at it, so the heart was invisible on every surface and the
 * feature looked unbuilt. A control that hides itself when it has nothing to
 * say cannot be found by the people who need to learn it exists.
 *
 * It goes on the tile as well as on photo detail, deliberately. `PhotoTile` is
 * rendered by the gallery grid and by both ambient strips, so putting it here
 * lights up liking on every surface a guest scrolls. Detail-only would be safer
 * against accidental taps — but a threshold of three needs likes to actually
 * happen, and two taps per like across six thousand photos means they will not.
 * An accidental like is one tap to undo, which is the cheaper failure.
 *
 * **One rule for everybody: the heart is a like, and the count always moves.**
 * An earlier version made it curate for organisers, which put a glyph and a
 * number next to each other that were talking about different things — tapping
 * changed the wall while the count beside it sat still, because nobody had
 * liked the photo. What makes an organiser's tap curation is what the server
 * does with it (D52 as amended: their like features the photo outright), not a
 * different button on this side.
 *
 * A guest cannot like their own photo — at a threshold of three that would make
 * the real bar two — so there the heart is the count without a button rather
 * than a button certain to be refused. An organiser can, because their like is
 * a promotion rather than a vote, and it is how they curate their own uploads
 * from the grid.
 *
 * Whether a photo is on the wall is a different fact and gets its own mark on
 * the tile, rather than being folded into this one.
 */
export default function LikeButton({ item, size = 'sm' }: { item: MediaItem; size?: 'sm' | 'lg' }) {
  const session = useSession();
  const liked = useLiked().has(item.id);
  const glyph = size === 'lg' ? 18 : 13;

  // `state !== 'done'` is this browser's own upload, still in flight: there is
  // no row on the server to like yet.
  const live = Boolean(session) && item.state === 'done';
  const canAct = live && (Boolean(session?.isAdmin) || item.uploaderId !== session?.userId);

  const on = liked;
  const face = <><IconHeart size={glyph} /> <span className="like-n">{item.likeCount}</span></>;

  if (!canAct) {
    return (
      <span
        className={`like like-${size} is-static${on ? ' is-on' : ''}`}
        aria-label={`${item.likeCount} likes`}
      >
        {face}
      </span>
    );
  }

  const label = on
    ? `Remove your like — ${item.likeCount} likes`
    : `Like this photo — ${item.likeCount} likes`;

  return (
    <button
      type="button"
      className={`like like-${size}${on ? ' is-on' : ''}`}
      aria-pressed={on}
      aria-label={label}
      onClick={(e) => {
        // The tile is a `<Link>`. Without this, the tap navigates instead —
        // which is exactly what a heart with no handler on it does.
        e.preventDefault();
        e.stopPropagation();
        toggleLike(item.id);
      }}
    >
      {face}
    </button>
  );
}
